import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Plus, X, Loader2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brand } from "@/components/common";
import { WEEKDAYS } from "@/lib/format";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STEPS = ["Empresa", "Local", "Áreas", "Turnos", "Equipos", "Usuarios", "Resumen", "Comenzar"];

export default function Onboarding() {
  const nav = useNavigate();
  const { user, loadMe } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [org, setOrg] = useState({ name: "", legal_name: "", tax_id: "", country: "Chile", timezone: "America/Santiago" });
  const [loc, setLoc] = useState({ name: "", internal_code: "", address: "", city: "", region: "", country: "Chile", timezone: "America/Santiago", phone: "", notes: "" });
  const [locId, setLocId] = useState(null);
  const [areas, setAreas] = useState(["Cocina", "Barra", "Bodega", "Producción"]);
  const [shifts, setShifts] = useState([{ name: "Turno Mañana", reference_start_time: "08:00", tolerance_minutes: 45, weekdays: [1, 2, 3, 4, 5, 6, 7] }]);
  const [equipos, setEquipos] = useState([{ name: "Refrigerador 1", min_temperature: "1", max_temperature: "4" }]);
  const [users, setUsers] = useState([{ name: "", pin: "", role: "USER" }]);

  useEffect(() => { api.get("/org").then(({ data }) => setOrg((o) => ({ ...o, ...data, name: data.name === "Demo Restaurant" ? "" : data.name }))).catch(() => {}); }, []);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submitOrg = async () => { if (!org.name) { toast.error("Nombre comercial requerido"); return; } setBusy(true); try { await api.put("/org", org); next(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };

  const submitLoc = async () => {
    if (!loc.name) { toast.error("Nombre del local requerido"); return; }
    setBusy(true);
    try {
      if (locId) { await api.put(`/locations/${locId}`, loc); }
      else { const { data } = await api.post("/locations", loc); setLocId(data.id); }
      next();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const submitAreas = async () => {
    setBusy(true);
    try { for (const a of areas.filter(Boolean)) await api.post("/areas", { location_id: locId, name: a }); next(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const submitShifts = async () => {
    setBusy(true);
    try { for (const s of shifts) await api.post("/shifts", { location_id: locId, ...s, tolerance_minutes: Number(s.tolerance_minutes), active: true }); next(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const submitEquipos = async () => {
    setBusy(true);
    try {
      const { data: types } = await api.get("/equipment-types");
      const { data: areaList } = await api.get(`/areas?location_id=${locId}`);
      const { data: shiftList } = await api.get(`/shifts?location_id=${locId}`);
      const typeId = types[0]?.id, areaId = areaList[0]?.id, shiftIds = shiftList.map((s) => s.id);
      for (const e of equipos.filter((e) => e.name)) {
        await api.post("/equipment", { location_id: locId, area_id: areaId, equipment_type_id: typeId, name: e.name, min_temperature: Number(e.min_temperature), max_temperature: Number(e.max_temperature), unit: "C", required_shift_ids: shiftIds });
      }
      next();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const submitUsers = async () => {
    setBusy(true);
    try { for (const u of users.filter((u) => u.name && u.pin)) await api.post("/users", { name: u.name, pin: u.pin, role: u.role, location_ids: [locId] }); next(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const finish = async () => { setBusy(true); try { await api.post("/org/onboarding/complete"); await loadMe(); nav("/admin"); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-5 py-8">
        <Brand descriptor />
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-slate-400"><span>Paso {step + 1} de {STEPS.length}</span><span>{STEPS[step]}</span></div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200"><div className="h-full bg-primary transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
          {step === 0 && <Section title="Datos de empresa">
            <F label="Nombre comercial *" v={org.name} on={(v) => setOrg({ ...org, name: v })} t="ob-org-name" />
            <F label="Razón social" v={org.legal_name} on={(v) => setOrg({ ...org, legal_name: v })} />
            <F label="RUT / ID fiscal" v={org.tax_id} on={(v) => setOrg({ ...org, tax_id: v })} />
            <div className="grid grid-cols-2 gap-3"><F label="País" v={org.country} on={(v) => setOrg({ ...org, country: v })} /><F label="Zona horaria" v={org.timezone} on={(v) => setOrg({ ...org, timezone: v })} /></div>
          </Section>}

          {step === 1 && <Section title="Primer local">
            <F label="Nombre *" v={loc.name} on={(v) => setLoc({ ...loc, name: v })} t="ob-loc-name" />
            <div className="grid grid-cols-2 gap-3"><F label="Código interno" v={loc.internal_code} on={(v) => setLoc({ ...loc, internal_code: v })} /><F label="Ciudad" v={loc.city} on={(v) => setLoc({ ...loc, city: v })} /></div>
            <F label="Dirección" v={loc.address} on={(v) => setLoc({ ...loc, address: v })} />
          </Section>}

          {step === 2 && <Section title="Áreas" desc="Edita, agrega o elimina según tu operación.">
            {areas.map((a, i) => (
              <div key={i} className="flex gap-2"><Input value={a} onChange={(e) => setAreas(areas.map((x, j) => j === i ? e.target.value : x))} className="h-11" /><Button variant="ghost" size="icon" onClick={() => setAreas(areas.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button></div>
            ))}
            <Button variant="outline" onClick={() => setAreas([...areas, ""])} className="rounded-xl"><Plus className="mr-2 h-4 w-4" /> Agregar área</Button>
          </Section>}

          {step === 3 && <Section title="Turnos">
            {shifts.map((s, i) => (
              <div key={i} className="space-y-2 rounded-xl border border-slate-200 p-3">
                <div className="grid grid-cols-3 gap-2">
                  <F label="Nombre" v={s.name} on={(v) => setShifts(shifts.map((x, j) => j === i ? { ...x, name: v } : x))} />
                  <div><Label className="text-xs">Apertura</Label><Input type="time" value={s.reference_start_time} onChange={(e) => setShifts(shifts.map((x, j) => j === i ? { ...x, reference_start_time: e.target.value } : x))} className="mt-1 h-11" /></div>
                  <div><Label className="text-xs">Tolerancia</Label><Input type="number" value={s.tolerance_minutes} onChange={(e) => setShifts(shifts.map((x, j) => j === i ? { ...x, tolerance_minutes: e.target.value } : x))} className="mt-1 h-11 tnum" /></div>
                </div>
                <div className="flex flex-wrap gap-1.5">{WEEKDAYS.map((d) => <button key={d.value} onClick={() => setShifts(shifts.map((x, j) => j === i ? { ...x, weekdays: x.weekdays.includes(d.value) ? x.weekdays.filter((y) => y !== d.value) : [...x.weekdays, d.value] } : x))} className={`rounded-lg px-2 py-1 text-xs ${s.weekdays.includes(d.value) ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-500"}`}>{d.label}</button>)}</div>
              </div>
            ))}
            <Button variant="outline" onClick={() => setShifts([...shifts, { name: "", reference_start_time: "16:00", tolerance_minutes: 45, weekdays: [1, 2, 3, 4, 5, 6, 7] }])} className="rounded-xl"><Plus className="mr-2 h-4 w-4" /> Agregar turno</Button>
          </Section>}

          {step === 4 && <Section title="Equipos" desc="Define al menos un equipo con su rango.">
            {equipos.map((e, i) => (
              <div key={i} className="grid grid-cols-4 gap-2">
                <div className="col-span-2"><Input placeholder="Nombre" value={e.name} onChange={(ev) => setEquipos(equipos.map((x, j) => j === i ? { ...x, name: ev.target.value } : x))} className="h-11" /></div>
                <Input placeholder="Mín" type="number" value={e.min_temperature} onChange={(ev) => setEquipos(equipos.map((x, j) => j === i ? { ...x, min_temperature: ev.target.value } : x))} className="h-11 tnum" />
                <Input placeholder="Máx" type="number" value={e.max_temperature} onChange={(ev) => setEquipos(equipos.map((x, j) => j === i ? { ...x, max_temperature: ev.target.value } : x))} className="h-11 tnum" />
              </div>
            ))}
            <Button variant="outline" onClick={() => setEquipos([...equipos, { name: "", min_temperature: "", max_temperature: "" }])} className="rounded-xl"><Plus className="mr-2 h-4 w-4" /> Agregar equipo</Button>
          </Section>}

          {step === 5 && <Section title="Usuarios" desc="Crea usuarios operacionales con PIN.">
            {users.map((u, i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <div className="col-span-2"><Input placeholder="Nombre" value={u.name} onChange={(e) => setUsers(users.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="h-11" /></div>
                <Input placeholder="PIN" value={u.pin} onChange={(e) => setUsers(users.map((x, j) => j === i ? { ...x, pin: e.target.value } : x))} className="h-11 tnum" />
              </div>
            ))}
            <Button variant="outline" onClick={() => setUsers([...users, { name: "", pin: "", role: "USER" }])} className="rounded-xl"><Plus className="mr-2 h-4 w-4" /> Agregar usuario</Button>
          </Section>}

          {step === 6 && <Section title="Resumen">
            <ul className="space-y-2 text-sm text-slate-600">
              <li>✓ Empresa: <b>{org.name}</b></li>
              <li>✓ Local: <b>{loc.name}</b></li>
              <li>✓ Áreas: {areas.filter(Boolean).join(", ")}</li>
              <li>✓ Turnos: {shifts.map((s) => s.name).join(", ")}</li>
              <li>✓ Equipos: {equipos.filter((e) => e.name).length}</li>
              <li>✓ Usuarios: {users.filter((u) => u.name && u.pin).length}</li>
            </ul>
          </Section>}

          {step === 7 && <div className="py-8 text-center">
            <PartyPopper className="mx-auto h-12 w-12 text-primary" />
            <h2 className="mt-3 font-display text-2xl font-semibold">¡Todo listo!</h2>
            <p className="mt-1 text-sm text-slate-500">Tu empresa está configurada. Comienza a registrar controles.</p>
          </div>}

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" onClick={back} disabled={step === 0 || busy}><ArrowLeft className="mr-2 h-4 w-4" /> Atrás</Button>
            {step === 0 && <Nav onClick={submitOrg} busy={busy} />}
            {step === 1 && <Nav onClick={submitLoc} busy={busy} />}
            {step === 2 && <Nav onClick={submitAreas} busy={busy} />}
            {step === 3 && <Nav onClick={submitShifts} busy={busy} />}
            {step === 4 && <Nav onClick={submitEquipos} busy={busy} />}
            {step === 5 && <Nav onClick={submitUsers} busy={busy} />}
            {step === 6 && <Button data-testid="ob-next-btn" onClick={next} className="rounded-xl">Continuar <ArrowRight className="ml-2 h-4 w-4" /></Button>}
            {step === 7 && <Button data-testid="ob-finish-btn" onClick={finish} disabled={busy} className="rounded-xl">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Comenzar <Check className="ml-2 h-4 w-4" /></>}</Button>}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Section({ title, desc, children }) {
  return <div className="space-y-3"><div><h2 className="font-display text-xl font-semibold">{title}</h2>{desc && <p className="text-sm text-slate-500">{desc}</p>}</div>{children}</div>;
}
function F({ label, v, on, t }) { return <div><Label className="text-sm">{label}</Label><Input data-testid={t} value={v || ""} onChange={(e) => on(e.target.value)} className="mt-1 h-11" /></div>; }
function Nav({ onClick, busy }) { return <Button data-testid="ob-next-btn" onClick={onClick} disabled={busy} className="rounded-xl">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continuar <ArrowRight className="ml-2 h-4 w-4" /></>}</Button>; }
